import { useCallback, useState } from "react";
import { ProjectOverviewButton } from "@/components/ProjectOverviewButton";
import { useTimesheetFlow } from "@/features/timesheetFlow/useTimesheetFlow";
import { useCurrentUser, userIsBuero } from "@/lib/auth";
import {
  isElectronWithOpenFolder,
  openElectronInOtherWindow,
  otherWindowToastMessage,
} from "@/lib/electron";
import { getOrder } from "@/lib/projectOverview";
import { openOrderFolder } from "@/lib/projects";
import { toast } from "@/lib/toast";
import { useProjectOverview } from "@/projectOverview/useProjectOverview";
import type { TimesheetRecord } from "@/features/timesheetFlow/timesheetFlowTypes";

const ICON_BTN =
  "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-separator bg-card text-label transition hover:bg-fill-tertiary disabled:opacity-40";

function timesheetHasFolder(ts: TimesheetRecord): boolean {
  return Boolean(
    ts.ordnerpfad ||
      ts.pfad ||
      ts.sharepoint_path ||
      ts.sharepoint_folder_id ||
      ts.has_folder,
  );
}

export function TimesheetProjectFooterIcons({ timesheet }: { timesheet: TimesheetRecord }) {
  const { user } = useCurrentUser();
  const { open: openProjectOverview } = useProjectOverview();
  const { close: closeTimesheet } = useTimesheetFlow();
  const [folderBusy, setFolderBusy] = useState(false);

  const auftragId = Number(timesheet.auftrag_id);
  const hasProject = Number.isFinite(auftragId) && auftragId > 0;
  const showFolder =
    userIsBuero(user) && isElectronWithOpenFolder() && (timesheetHasFolder(timesheet) || hasProject);

  const handleProjectOverview = useCallback(
    (id: number) => {
      // Detail schließen: über Rechnungsentwurf liegt der Stundennachweis (z-320)
      // sonst über der Projektübersicht (z-310).
      closeTimesheet();
      void openElectronInOtherWindow(`/projekte?auftrag_id=${id}`).then((target) => {
        if (target) {
          toast.info(otherWindowToastMessage(target));
          return;
        }
        openProjectOverview(id);
      });
    },
    [closeTimesheet, openProjectOverview],
  );

  if (!hasProject && !showFolder) return null;

  const handleOpenFolder = async () => {
    if (folderBusy || !hasProject) return;
    setFolderBusy(true);
    try {
      const order = await getOrder(auftragId);
      await openOrderFolder(order);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Ordner konnte nicht geöffnet werden.");
    } finally {
      setFolderBusy(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-1.5">
      {hasProject ? (
        <ProjectOverviewButton
          auftragId={auftragId}
          title="Projektübersicht"
          onOpenOverride={handleProjectOverview}
        />
      ) : null}
      {showFolder ? (
        <button
          type="button"
          className={ICON_BTN}
          disabled={folderBusy}
          title="Ordner öffnen"
          aria-label="Ordner öffnen"
          onClick={() => void handleOpenFolder()}
        >
          <span className="text-[1.2rem] leading-none" aria-hidden>
            📂
          </span>
        </button>
      ) : null}
    </div>
  );
}
